/* eslint-disable lingui/no-unlocalized-strings -- caminhos HTTP, valores técnicos do fio e rótulos de log, não texto de UI */
import { listCreditProducts, type CreditProductListParams } from '@/modules/credit-product/public';
import { listPayrollAgreements } from '@/modules/payroll-agreement/public';
import { creditApi } from '@/shared/http/client';
import { wire, withSignal } from '@/shared/reference/wire';

import {
  payrollBatchResponseSchema,
  payrollProductListSchema,
  siapeMarginListSchema,
  zetraMarginListSchema,
  type PayrollAgreementItem,
  type PayrollBatchModel,
  type PayrollProduct,
  type SiapeMargin,
  type ZetraMargin,
} from './simulation-payroll.wire';

// Chamadas dos consignados, com o contrato do legado. Todas na API de crédito.

/**
 * Convênios (`useGetListPayrollAgreement()`, PayrollAgreementContext.tsx:18-90, sem página, tamanho nem tenant): o
 * `searchString` digitado (só quando há texto) e `orderBy=createdAt_DESC`, pela porta do `payroll-agreement`.
 */
export async function searchPayrollAgreements(
  searchString: string,
  signal?: AbortSignal,
): Promise<PayrollAgreementItem[]> {
  const page = await listPayrollAgreements(
    { searchString: searchString === '' ? undefined : searchString },
    signal,
  );
  return page.data;
}

export const PUBLIC_PAYROLL_CATEGORY = 'CONSIGNADO PÚBLICO';

/**
 * Produtos do convênio (`useCreditProdutList(applyFilteredQueryProduct(...))`, SimulateContainer.tsx:143-149,
 * GenericMethods.ts:205-223, creditProductContext.tsx:141): página 0, 20 itens, a categoria, o convênio, o prazo de
 * postagem e o `tenant` do correspondente, pela porta do `credit-product`. Só com `CreditProduct:Read`.
 */
export async function listPayrollProducts(
  input: {
    readonly categoryName: string;
    readonly payrollAgreementId: string;
    readonly tenant: string | undefined;
  },
  signal?: AbortSignal,
): Promise<PayrollProduct[]> {
  // `payrollAgreementId` não está no tipo da porta; o objeto vai inteiro para a query (o `listCreditProducts` espalha).
  const params: CreditProductListParams & { readonly payrollAgreementId: string } = {
    page: 0,
    size: 20,
    categoryName: input.categoryName,
    payrollAgreementId: input.payrollAgreementId,
    isConsiderPostingDeadline: true,
    tenant: input.tenant,
  };
  const page = await listCreditProducts(params, signal);
  return wire(payrollProductListSchema, page.data, 'GET /Product (consignado)');
}

/**
 * `simulationAmortizationBatch` (creditNote.ts:709-716): `POST /Amortization/Batch` com os modelos crus (sem
 * `toDataModel`), sem tenant, timeout padrão. A resposta é uma lista, um item por modelo.
 */
export async function postAmortizationBatch(
  models: readonly PayrollBatchModel[],
  signal?: AbortSignal,
): Promise<unknown[]> {
  const data = await creditApi.post('/Amortization/Batch', models, withSignal(signal));
  return wire(payrollBatchResponseSchema, data, 'POST /Amortization/Batch');
}

/**
 * A margem SIAPE guardada (`getQueryMarginDataset`, datasets.ts:45-51, como o passo Simular chama ao abrir em
 * SimulateContainer.tsx:155-160): só o `registrationNumber`. Só lê o último dataset (sem chamar a averbadora, sem
 * custo); sem dataset o backend responde `200` sem corpo (`return Ok()`), que vira lista vazia.
 */
export async function getSiapeMargin(
  registrationNumber: string,
  signal?: AbortSignal,
): Promise<SiapeMargin[]> {
  const data = await creditApi.get<unknown>('/Siape/FreeMarginQuery', {
    params: { registrationNumber },
    ...withSignal(signal),
  });
  return Array.isArray(data) ? wire(siapeMarginListSchema, data, 'GET /Siape/FreeMarginQuery') : [];
}

/**
 * **Consulta nova ao SIAPE, paga** (`postQueryMarginDataset`, datasets.ts:53-59, "Obter margem"/"Atualizar margem" de
 * MarginQueryToNewSimulation.tsx:134-143): corpo `{}` e o `registrationNumber` na query. É a única chamada que vai à
 * averbadora: prévia e clique (X01).
 */
export async function requestSiapeMargin(registrationNumber: string): Promise<unknown> {
  return creditApi.post('/Siape/FreeMarginQuery', {}, { params: { registrationNumber } });
}

/**
 * A margem Zetra guardada (`getZetraMarginQueryAsync`, zetra.ts:65-69, como o passo Simular chama ao abrir em
 * SimulateContainer.tsx:162-166): `registrationNumber` e, com matrícula, `employeeCode`. Só leitura, sem custo; sem
 * dado o backend responde 4xx, que vira toast.
 */
export async function getZetraMargin(
  input: { readonly registrationNumber: string; readonly employeeCode: string | null | undefined },
  signal?: AbortSignal,
): Promise<ZetraMargin[]> {
  const params: Record<string, string> = { registrationNumber: input.registrationNumber };
  if (input.employeeCode) params['employeeCode'] = input.employeeCode;
  const data = await creditApi.get<unknown>('/Zetra/FreeMarginQuery', {
    params,
    ...withSignal(signal),
  });
  return wire(zetraMarginListSchema, data, 'GET /Zetra/FreeMarginQuery');
}

